import { useState } from 'react'
import { PreviewJD } from '@/interfaces/answer'
import { Dialog, DialogContent, DialogClose, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { CheckIcon, RotateCcwIcon } from 'lucide-react'
import { LoadingScreen } from '@/components/loading-screen'
import { Switch } from '@/components/ui/switch'
import { JDCard } from '@/components/JDCard'

interface ReviewJDDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (isAnonymous: boolean) => void
  previewJD: PreviewJD | null
  personName?: string
  userRole?: string
  isLoading?: boolean
  isConfirming?: boolean
  isForOthers?: boolean // true for SendJD, false for CreateJD
}

export const ReviewJDDialog = ({
  open,
  onOpenChange,
  onConfirm,
  previewJD,
  personName,
  userRole,
  isLoading = false,
  isConfirming = false,
  isForOthers = false,
}: ReviewJDDialogProps) => {
  const [isAnonymous, setIsAnonymous] = useState(false)

  const handleConfirm = () => {
    onConfirm(isAnonymous)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100%-2rem)] sm:max-w-5xl max-h-[95vh] overflow-y-auto bg-[#FF9DBE] p-0 border-0"
        showCloseButton={false}
      >
        {/* Loading State */}
        {isLoading && (
          <div className="p-12">
            <LoadingScreen message="Đang tạo JD của bạn..." />
          </div>
        )}

        {/* Pink Background with content */}
        {!isLoading && (
          <div className="bg-[#FF9DBE] relative w-full rounded-lg">
            {/* Title */}
            <div className="py-8 text-center">
              <h2 className="font-momo-trust-display text-[40px] font-bold text-black">
                Untold JD của {personName || 'Bạn'}
              </h2>
            </div>

            {/* White Card Content */}
            <div className="rounded-t-3xl mx-8 mb-6">
              {previewJD && (
                <JDCard
                  name={personName || 'Bạn'}
                  role={previewJD.role}
                  userRole={userRole}
                  notableQualities={previewJD.notableQualities}
                  mainResponsibilities={previewJD.mainResponsibilities}
                  requirements={previewJD.requirements}
                  showRibbon={false}
                />
              )}
            </div>

            {/* Disclaimer Text */}
            <div className="px-6 pb-3 text-center w-[70%] mx-auto">
              <p className="text-base leading-relaxed text-white">
                AI đã gói ghém lại từ nội dung bạn đã viết. Hãy nhấn “Thử lại” nếu muốn thử 1 phiên
                bản khác hoặc nhấn “Duyệt” nếu bạn thích nội dung này.
              </p>
            </div>

            {/* Anonymous Switch Panel - Only show for SendJD */}
            {isForOthers && (
              <div className="flex justify-center px-6 pb-4">
                <div className="w-fit bg-white/90 flex items-center gap-4 px-6 py-3 rounded-full shadow-sm">
                  <label
                    htmlFor="anonymous"
                    className="text-base font-medium text-gray-700 cursor-pointer select-none"
                  >
                    Gửi ẩn danh
                  </label>
                  <Switch
                    id="anonymous"
                    checked={isAnonymous}
                    onCheckedChange={setIsAnonymous}
                    className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-gray-300"
                  />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <DialogFooter className="sm:justify-center flex-row items-center justify-center gap-4 px-6 pb-8">
              <DialogClose asChild>
                <Button size={null} className="btn-primary w-fit" disabled={isConfirming}>
                  <RotateCcwIcon className="size-7" />
                  Thử lại
                </Button>
              </DialogClose>
              <Button
                onClick={handleConfirm}
                disabled={isConfirming}
                size={null}
                className="btn-primary w-fit"
              >
                <CheckIcon className="size-7" />
                {isConfirming ? 'Đang xử lý...' : 'Duyệt'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
